// src/components/ceremony/CeremonyCanvas.js
// ============================================================================
// Shared chrome for every ceremony surface.
//
// The founder-approved designs (Media/ShowdownDemo/assets/{startingsoon,
// loading,endscreen}.html) are 1920x1080 compositions. Rather than re-deriving
// those layouts responsively — and losing the composition he signed off on —
// this renders the design at its native 1920x1080 and scales the whole canvas
// to fit whatever box the video area happens to be. Same trick a broadcast
// overlay uses; every pixel value inside a ceremony screen is a DESIGN pixel.
//
// Motion follows the app's existing web idiom (CyberpunkBackground,
// TsunamiMeter's RumbleBurst): inject a <style> block of @keyframes once,
// guard on web + document, and always ship a prefers-reduced-motion escape.
// ============================================================================

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, Image, StyleSheet, Platform } from 'react-native';

import {
  CEREMONY_COLORS as C,
  CEREMONY_BACKDROP_VIDEO_URL,
  CEREMONY_BACKDROP_POSTER_URL,
} from '../../constants/ceremony';

export const IS_WEB = Platform.OS === 'web';

export const DESIGN_W = 1920;
export const DESIGN_H = 1080;

// RN Web hands back a URL string (or a { uri } / { default } wrapper) from
// require() — the same normalizer TsunamiMeter and Header hand-roll.
export const resolveSrc = (m) => (m && (m.uri || m.default)) || m;

export const LOGO = require('../../../assets/critcash_logo_transparent.webp');
// Dice semantics (verified against the art): plain _dice = GOLD (brand mark),
// _gdice = GREEN, _pdice = PURPLE. The approved grid-lock screen flies GREEN
// dice into the sockets; the results screen rains PURPLE.
export const DIE_GREEN_20 = require('../../../assets/20_gdice.webp');
export const DIE_PURPLE_20 = require('../../../assets/20_pdice.webp');
export const DIE_PURPLE_1 = require('../../../assets/1_pdice.webp');

// ---------------------------------------------------------------------------
// keyframes
// ---------------------------------------------------------------------------

// Raw DOM elements (the flying/raining dice) still use injected CSS, because
// they need per-element custom properties that a style object can't express.
// RN Views do NOT: react-native-web rejects `animationName` outright and wants
// `animationKeyframes` instead, so View motion is declared in JS below.
let _injected = false;
export function injectCeremonyStyles() {
  if (_injected || !IS_WEB || typeof document === 'undefined') return;
  _injected = true;
  const css = `
  @keyframes ccCerDieRain {
    0% { transform: translateY(-140px) rotate(0deg); opacity: 0; }
    10% { opacity: 1; }
    100% { transform: translateY(760px) rotate(var(--cc-cer-rot, 540deg)); opacity: 0; }
  }
  /* A die thrown from the crown into a grid socket. Every slot shares this one
     keyframe and supplies its own endpoint through --cc-dx / --cc-dy, so the
     whole 16-slot lock sequence costs one CSS rule. */
  @keyframes ccCerDieFly {
    0%   { transform: translate(0px, 0px) scale(0.5) rotate(0deg); opacity: 0; }
    12%  { transform: translate(0px, 0px) scale(0.72) rotate(40deg); opacity: 1; }
    55%  { transform: translate(calc(var(--cc-dx) * 0.5), calc(var(--cc-dy) * 0.5 - 120px)) scale(1.12) rotate(300deg); opacity: 1; }
    97%  { transform: translate(var(--cc-dx), var(--cc-dy)) scale(0.62) rotate(660deg); opacity: 1; }
    100% { transform: translate(var(--cc-dx), var(--cc-dy)) scale(0.62) rotate(660deg); opacity: 0; }
  }
  .cc-cer-die { position: absolute; will-change: transform, opacity;
    animation-name: ccCerDieRain; animation-timing-function: cubic-bezier(.3,.1,.6,1); animation-fill-mode: both; }
  .cc-cer-fly { position: absolute; will-change: transform, opacity;
    animation-name: ccCerDieFly; animation-fill-mode: both; }

  @media (prefers-reduced-motion: reduce) {
    .cc-cer-die, .cc-cer-fly { display: none; }
  }`;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------
// View motion, in the API react-native-web actually accepts.
// ---------------------------------------------------------------------------

export const KEYFRAMES = {
  fadeDown: [{ '0%': { opacity: 0, transform: 'translateY(-24px)' }, '100%': { opacity: 1, transform: 'translateY(0px)' } }],
  fadeUp: [{ '0%': { opacity: 0, transform: 'translateY(18px)' }, '100%': { opacity: 1, transform: 'translateY(0px)' } }],
  fadeIn: [{ '0%': { opacity: 0 }, '100%': { opacity: 1 } }],
  fadeOut: [{ '0%': { opacity: 1 }, '100%': { opacity: 0 } }],
  popIn: [{ '0%': { opacity: 0, transform: 'scale(1.6)' }, '100%': { opacity: 1, transform: 'scale(1)' } }],
  stampIn: [{
    '0%': { opacity: 0, transform: 'scale(2.4)' },
    '60%': { opacity: 1, transform: 'scale(1.08)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  }],
  lockFlash: [{ '0%': { opacity: 0 }, '25%': { opacity: 1 }, '100%': { opacity: 0.55 } }],
  soonPulse: [{ '0%': { opacity: 0.86 }, '50%': { opacity: 1 }, '100%': { opacity: 0.86 } }],
};

// Spectators who ask for less motion get the finished frame, not a blank one.
const prefersReducedMotion = () => {
  if (!IS_WEB || typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (err) {
    return false;
  }
};

/**
 * A style fragment, NOT props — compose it into the style array:
 *   <View style={[styles.header, anim('fadeDown', { delay: 200 })]} />
 *
 * (Spreading it as props would silently drop the element's own `style`.)
 * On native, and under reduced motion, this is a no-op and the element renders
 * in its final state — no ceremony beat depends on motion to be legible.
 */
export function anim(name, { delay = 0, duration = 600, easing, iteration } = {}) {
  if (!IS_WEB || prefersReducedMotion()) return null;
  const frames = KEYFRAMES[name];
  if (!frames) return null;
  return {
    animationKeyframes: frames,
    animationDelay: `${delay}ms`,
    animationDuration: `${duration}ms`,
    animationFillMode: 'both',
    animationTimingFunction: easing || 'cubic-bezier(.2,.8,.2,1)',
    ...(iteration ? { animationIterationCount: iteration } : {}),
  };
}

// ---------------------------------------------------------------------------
// canvas
// ---------------------------------------------------------------------------

export default function CeremonyCanvas({ children, style, backdrop = 'grid' }) {
  const [box, setBox] = useState({ w: 0, h: 0 });
  const hostRef = useRef(null);

  // The raw-DOM dice (fly + rain) animate off injected classes; RN Views use
  // animationKeyframes and need nothing here.
  injectCeremonyStyles();

  const apply = useCallback((width, height) => {
    if (!width || !height) return;
    setBox((prev) => (prev.w === width && prev.h === height ? prev : { w: width, h: height }));
  }, []);

  const onLayout = useCallback((e) => {
    const { width, height } = e.nativeEvent.layout;
    apply(width, height);
  }, [apply]);

  // onLayout alone did NOT fire for this absolutely-filled host, and a canvas
  // that never learns its size renders nothing at all — so measure the node
  // directly and watch it. Same lesson useViewportWidth already learned on this
  // screen: don't trust a single layout signal, subscribe to the real one.
  useEffect(() => {
    if (!IS_WEB || typeof window === 'undefined') return undefined;
    const node = hostRef.current;
    if (!node || typeof node.getBoundingClientRect !== 'function') return undefined;

    const measure = () => {
      const r = node.getBoundingClientRect();
      apply(Math.round(r.width), Math.round(r.height));
    };
    measure();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    }
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // Fullscreen changes the box without always firing resize.
    document.addEventListener('fullscreenchange', measure);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [apply]);

  const scale = box.w && box.h ? Math.min(box.w / DESIGN_W, box.h / DESIGN_H) : 0;

  return (
    <View ref={hostRef} style={[styles.root, style]} onLayout={onLayout} pointerEvents="box-none">
      {/* MEDIA fills the whole box, outside the scaled canvas: letterboxing a
          hype video with the composition would band it with black bars on any
          player that isn't exactly 16:9, which is most of them. */}
      {backdrop !== 'none' && <CeremonyBackdropMedia variant={backdrop} />}

      {scale > 0 && (
        <View
          style={[styles.canvas, { transform: [{ scale }] }]}
          pointerEvents="box-none"
        >
          {/* TREATMENT stays inside the canvas, so the approved shade's dark
              top and dark floor land on the title and the ticker rather than
              somewhere out in the letterbox. */}
          {backdrop !== 'none' && <CeremonyBackdropTreatment variant={backdrop} />}
          {children}
        </View>
      )}
    </View>
  );
}

/**
 * Full-bleed looping banner video, under the pre-event beats.
 *
 * Founder: a hype loop that also showcases the games the platform runs. The
 * source is the CritCash banner montage already shipping on the landing page,
 * so this adds no weight to the build. Cover-cropped (the master is
 * letterboxed), muted, looping, with the approved shade over it.
 *
 * Mirrors LandingScreen's HeroVideoBackdrop exactly, including the reason it
 * touches the ref: React does not reliably reflect `muted` onto the DOM
 * attribute, and browsers refuse to autoplay video that isn't muted.
 */
function BannerVideoLayer() {
  if (!IS_WEB) return null;

  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const fill = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  if (reducedMotion) {
    return <img src={CEREMONY_BACKDROP_POSTER_URL} alt="" aria-hidden="true" style={fill} />;
  }

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={CEREMONY_BACKDROP_POSTER_URL}
      aria-hidden="true"
      ref={(el) => {
        if (!el) return;
        el.muted = true;
        const p = el.play();
        if (p && p.catch) p.catch(() => {});
      }}
      style={fill}
    >
      <source src={CEREMONY_BACKDROP_VIDEO_URL} type="video/mp4" />
    </video>
  );
}

/**
 * The shared backdrop from the approved screens: gold/cyan corner glows, a
 * masked cyber grid, and a vignette. Deliberately NO floating dots (founder
 * 2026-07: the landing backdrop direction rejected them).
 *
 * variant 'video' swaps the corner glows for the looping banner montage and a
 * heavier shade, matching the approved startingsoon.html.
 */
export function CeremonyBackdropMedia({ variant = 'grid' }) {
  const isVideo = variant === 'video';
  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      <View style={[StyleSheet.absoluteFill, { backgroundColor: C.BG }]} />
      {isVideo && <BannerVideoLayer />}
      {/* A scrim across the whole frame so the video reads as a backdrop even
          out in the letterbox, where the composition's own shade doesn't
          reach. It darkens in the SAME direction as that shade (heavy at the
          top and floor, open through the middle) so the two blend instead of
          leaving a visible seam at the canvas edge. Light enough through the
          middle that the gameplay still shows. */}
      {IS_WEB && isVideo && (
        <View
          style={[
            StyleSheet.absoluteFill,
            {
              backgroundImage:
                'linear-gradient(180deg, rgba(0,0,0,0.66) 0%, rgba(0,0,0,0.34) 22%,'
                + ' rgba(0,0,0,0.34) 74%, rgba(0,0,0,0.74) 100%)',
            },
          ]}
        />
      )}
    </View>
  );
}

export function CeremonyBackdropTreatment({ variant = 'grid' }) {
  const isVideo = variant === 'video';
  const gridAlpha = isVideo ? '0.05' : '0.045';
  const maskY = isVideo ? '48%' : '45%';
  const gridlines = IS_WEB
    ? {
      backgroundImage:
        `repeating-linear-gradient(0deg, rgba(252,208,0,${gridAlpha}) 0 1px, transparent 1px 96px),`
        + `repeating-linear-gradient(90deg, rgba(252,208,0,${gridAlpha}) 0 1px, transparent 1px 96px)`,
      WebkitMaskImage: `radial-gradient(ellipse 70% 60% at 50% ${maskY}, #000 30%, transparent 100%)`,
      maskImage: `radial-gradient(ellipse 70% 60% at 50% ${maskY}, #000 30%, transparent 100%)`,
    }
    : null;

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      {/* The static variants own their background; the video variant must not
          paint one or it would cover the loop. */}
      {!isVideo && <View style={[StyleSheet.absoluteFill, { backgroundColor: C.BG }]} />}

      {IS_WEB && isVideo && (
        // The approved shade, aligned to the COMPOSITION: dark at the top so
        // the logo/title read, lighter through the middle so the gameplay
        // shows, dark again at the floor for the ticker and sponsor slot.
        <View
          style={[
            StyleSheet.absoluteFill,
            {
              backgroundImage:
                'linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.30) 30%,'
                + ' rgba(0,0,0,0.38) 62%, rgba(0,0,0,0.85) 100%)',
            },
          ]}
        />
      )}

      {/* Corner glows belong to the static variants only — over the video they
          would just wash out the gameplay the loop exists to show. */}
      {IS_WEB && !isVideo && (
        <>
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundImage:
                  'radial-gradient(closest-side, rgba(252,208,0,0.10), transparent 72%)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1100px 700px',
                backgroundPosition: variant === 'podium' ? 'center -380px' : '-280px -260px',
              },
            ]}
          />
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundImage:
                  'radial-gradient(closest-side, rgba(41,196,232,0.09), transparent 72%)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1100px 700px',
                backgroundPosition: 'calc(100% + 300px) calc(100% + 280px)',
              },
            ]}
          />
        </>
      )}

      {/* Grid + vignette sit over EVERY variant, video included — that's what
          ties the banner loop back to the brand backdrop. */}
      {IS_WEB && (
        <>
          <View style={[StyleSheet.absoluteFill, gridlines]} />
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundImage: isVideo
                  ? 'radial-gradient(ellipse 85% 80% at 50% 45%, transparent 55%, rgba(0,0,0,0.7) 100%)'
                  : 'radial-gradient(ellipse 85% 80% at 50% 45%, transparent 55%, rgba(0,0,0,0.75) 100%)',
              },
            ]}
          />
        </>
      )}
    </View>
  );
}

/** The logo + event title block every ceremony screen opens with. */
export function CeremonyHeader({ title, subtitle, logoWidth = 236, top = 18, delay = 0 }) {
  return (
    <View style={[styles.header, { top }, anim('fadeDown', { delay, duration: 900 })]}>
      <Image
        source={LOGO}
        style={{ width: logoWidth, height: logoWidth * 0.34, alignSelf: 'center' }}
        resizeMode="contain"
      />
      {!!title && <Text style={styles.headerTitle} numberOfLines={1}>{title}</Text>}
      {!!subtitle && <Text style={styles.headerSubtitle} numberOfLines={1}>{subtitle}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: C.BG,
  },
  canvas: {
    width: DESIGN_W,
    height: DESIGN_H,
    position: 'relative',
  },
  header: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  headerTitle: {
    marginTop: 8,
    fontSize: 37,
    fontWeight: '900',
    letterSpacing: 9,
    color: C.GOLD,
    textAlign: 'center',
    ...(IS_WEB ? { textShadow: '0 0 28px rgba(252,208,0,0.45)' } : {}),
  },
  headerSubtitle: {
    marginTop: 5,
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 6,
    color: C.CYAN_SOFT,
    textAlign: 'center',
  },
});
